import React, { useState } from 'react'
import useFetchTableData from '../../hooks/useFetchTableData';
import adminApi from '../../api/admin';
import { formatDateForDisplay, formatDateToYMD, formatISOToDate } from '../../utils/helpers';
import Button from '../../components/common/Button';
import { Filter, RefreshCw, Search } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Link } from 'react-router-dom';

const AdminRecentActivity = () => {
    const initialFilters = {
        action: "all",
        search: "",
        start: "",
        end: "",
      };
      const [filters, setFilters] = useState(initialFilters);
    const {
    data,
    isError,
    error,
    isLoading,
    refetch,
    paginationModel,
    setPaginationModel,
  } = useFetchTableData({
    queryKey: "my-audit-trails",
    fetchFunc: adminApi.getMyRecentActivity,
    params: filters
  });

  const handleActionFilterChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      action: event.target.value,
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      search: event.target.value,
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const hasActiveFilters =
    filters.action !== "all" || filters.search || filters.start || filters.end;

    const columns = [
        {
          field: "login_email",
          headerName: "Login Email",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => (
            <div className="font-mono text-sm flex items-center">
              <Link
                to={`/admin/audit/${params.row.id}`}
                className="text-blue-600 hover:underline font-semibold"
              >
                {params.value || "N/A"}
              </Link>
            </div>
          ),
        },
        {
          field: "action",
          headerName: "Action",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => params.row.action_display,
        },
        {
          field: "ip_address",
          headerName: "IP Address",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => params.value || "N\A",
        },
        {
          field: "browser_name",
          headerName: "Browser Name",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => params.value || "N\A",
        },
        {
          field: "browser_os",
          headerName: "Browser OS",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => params.value || "N\A",
        },
        {
          field: "timestamp",
          headerName: "Timestamp",
          flex: 1,
          minWidth: 200,
          renderCell: (params) => formatDateForDisplay(params.value),
        },
      ];

      if (isError) {
            return (
              <div className="max-w-7xl mx-auto py-8 px-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">
                    Error loading audit trails: {error?.message}
                  </p>
                  <Button
                    onClick={() => refetch()}
                    variant="outline"
                    className="mt-4"
                    icon={<RefreshCw className="w-4 h-4 mr-2" />}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            );
          }
  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">
            My Recent Activity
          </h1>
          <p className="text-secondary-600 mt-1">
            {data
              ? `Total: ${data.count} activities`
              : "My Audit Trail"}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Button
            onClick={handleClearFilters}
            variant="outline"
            disabled={!hasActiveFilters}
            icon={<RefreshCw className="w-4 h-4 mr-2" />}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      <Card className="mb-6 shadow-sm border border-gray-200">
        <CardContent className="p-6">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Filter className="w-5 h-5 text-blue-600" />
                </div>
                <Typography
                  variant="subtitle1"
                  className="font-semibold text-gray-800"
                >
                  Filters
                </Typography>
              </div>
            </div>

            {/* Filters Grid */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
              <Grid container spacing={3}>
                {/* Search */}
                <Grid size={{xs: 12, md: 4}}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search email, IP, browser, OS"
                    value={filters.search}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search className="w-4 h-4 text-gray-400" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>

                {/* Action */}
                <Grid size={{xs: 12, md: 3}}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={filters.action}
                      label="Action"
                      onChange={handleActionFilterChange}
                    >
                      <MenuItem value="all">All Actions</MenuItem>
                      <MenuItem value="login_success">Login Success</MenuItem>
                      <MenuItem value="login_failed">Login Failed</MenuItem>
                      <MenuItem value="logout">Logout</MenuItem>
                      <MenuItem value="session_expired">
                        Session Expired
                      </MenuItem>
                      <MenuItem value="password_changed">
                        Password Changed
                      </MenuItem>
                      <MenuItem value="password_reset">Password Reset</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Start Date */}
                <Grid size={{xs: 12, sm: 6, md: 2.5}}>
                  <DatePicker
                    label="Start Date"
                    value={formatISOToDate(filters.start)}
                    onChange={(date) =>
                      setFilters((prev) => ({
                        ...prev,
                        start: formatDateToYMD(date),
                      }))
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>

                {/* End Date */}
                <Grid size={{xs: 12, sm: 6, md: 2.5}}>
                  <DatePicker
                    label="End Date"
                    value={formatISOToDate(filters.end)}
                    onChange={(date) =>
                      setFilters((prev) => ({
                        ...prev,
                        end: formatDateToYMD(date),
                      }))
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </LocalizationProvider>
        </CardContent>
      </Card>
      <DataTable
        rows={data?.results || []}
        columns={columns}
        loading={isLoading}
        rowCount={data?.count || 0}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />
    </div>
  )
}

export default AdminRecentActivity